import useCourseEditing from '@/data/hook/useCourseEditing';
import LoadingView from '@/pages/courses/LoadingView';
import LessonBar from '../LessonBar/LessonBar';
import CreateLessonListProps from './CreateLessonListProps';
import { ScheduledLessonTypeEnum } from '@/models/ScheduledLesson';
import InfinityList from '@/components/ui/custom/InfinityList';
import useScheduledLessons from '@/data/hook/useScheduledLessons';
import useInfiniteService from '@/data/hook/useInfiniteService';
import { lessonsQueryKeys } from '@/data/services/querykeys';
import { useValidatedScheduledLessons } from '@/data/hook/useValidatedScheduledLessons';
import { useEffect, useMemo } from 'react';
import ConditionalRenderer from '@/components/ui/custom/ConditionalRenderer';
import InvalidLesson from '@/pages/klass/lessons/InvalidLesson';
import useLessonProgresses from '@/data/hook/useLessonProgresses';
import useStudentContext from '@/data/hook/useStudentContext';
import StudentKlassLessons from '@/components/klass/student/StudentKlassLessons/StudentKlassLessons';

export default function LessonListManager({
  slugCourseName,
  klassId,
  onInvalidLessons,
}: CreateLessonListProps) {
  const { currentProgress } = useStudentContext();

  if (currentProgress) {
    return <StudentKlassLessons progress={currentProgress} />;
  }

  if (klassId)
    return (
      <ScheduledLessonList
        klassId={klassId}
        slugCourseName={slugCourseName}
        onInvalidLessons={onInvalidLessons}
      />
    );

  return <LessonList slugCourseName={slugCourseName} />;
}

type ScheduledLessonListProps = {
  klassId: number;
  slugCourseName: string;
  onInvalidLessons?: (hasInvalidLessons: boolean) => void;
};

function ScheduledLessonList({
  klassId,
  slugCourseName,
  onInvalidLessons,
}: ScheduledLessonListProps) {
  const { reorderScheduledLessons } = useCourseEditing();
  const {
    results: scheduledLessons,
    fetchNextPage,
    isFetchingNextPage,
    refetching,
    loading,
    hasNextPage,
  } = useScheduledLessons({ klassId });

  const { hasInvalidLessons } = useValidatedScheduledLessons({
    scheduledLessons,
    klassId,
  });

  const shouldUseReorderList = !!reorderScheduledLessons.length;

  const scheduledLessonList = shouldUseReorderList
    ? reorderScheduledLessons
    : scheduledLessons;

  useEffect(() => {
    if (onInvalidLessons) {
      onInvalidLessons(hasInvalidLessons);
    }
  }, [hasInvalidLessons, onInvalidLessons]);

  if (loading || refetching) {
    return <LoadingView />;
  }

  if (!scheduledLessonList.length) return null;

  return (
    <InfinityList
      isFetchingNextPage={isFetchingNextPage}
      hasNextPage={hasNextPage}
      onReachEnd={fetchNextPage}
      className="gap-4"
    >
      {scheduledLessonList.map((scheduledLesson, i) => {
        const isExtraLesson =
          scheduledLesson.type !== ScheduledLessonTypeEnum.NORMAL;

        return (
          <ConditionalRenderer
            key={scheduledLesson.id}
            condition={scheduledLesson.lesson?.id}
            fallback={
              <InvalidLesson index={i} scheduledLesson={scheduledLesson} />
            }
          >
            <LessonBar
              key={scheduledLesson.id}
              color={isExtraLesson ? 'accent' : 'secondary'}
              lesson={scheduledLesson.lesson}
              index={i}
              slugCourseName={slugCourseName}
              scheduledLesson={scheduledLesson}
              klassId={klassId}
            />
          </ConditionalRenderer>
        );
      })}
    </InfinityList>
  );
}

function LessonList({
  slugCourseName,
}: Pick<CreateLessonListProps, 'slugCourseName'>) {
  const {
    results: lessons,
    isInitialLoading,
    isRefetching,
    isFetchingNextPage: isFetchingNextLessonPage,
    hasNextPage,
    fetchNextPage,
  } = useInfiniteService({
    ...lessonsQueryKeys.nestedList(slugCourseName, {
      type: ScheduledLessonTypeEnum.NORMAL,
    })._ctx.infinity,
    keepPreviousData: true,
    enabled: !!slugCourseName,
  });

  const lessonIds = useMemo(() => lessons.map(l => l.id), [lessons]);

  const { lessonProgresses, studentView } = useLessonProgresses({
    lessonIds,
    orderBy: 'lesson_order',
  });

  if (isInitialLoading || isRefetching) {
    return <LoadingView />;
  }
  if (!lessons.length) return null;

  return (
    <InfinityList
      isFetchingNextPage={isFetchingNextLessonPage}
      hasNextPage={hasNextPage}
      onReachEnd={fetchNextPage}
      className="gap-4"
    >
      {lessons.map((lesson, i) => {
        const lessonProgressData = studentView
          ? lessonProgresses.find(
              ({ lesson: { id: lessonId } }) => lessonId === lesson.id,
            )
          : undefined;

        return (
          <LessonBar
            color="secondary"
            key={lesson.id}
            lesson={lesson}
            lessonProgress={lessonProgressData}
            index={i}
            slugCourseName={slugCourseName}
            isStaff={!studentView}
          />
        );
      })}
    </InfinityList>
  );
}
